import { ChainData, Token } from "@0xsquid/squid-types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { encodeFunctionData } from "viem";
import { usePublicClient } from "wagmi";
import { CHAIN_IDS, nativeEvmTokenAddress } from "../../core";
import hrc20 from "../../core/abis/hrc20.json";
import { getPrefixKey, keys, QueryKeys } from "../../core/queries/queries-keys";
import { parseEvmAddress } from "../../services";
import { HederaApiClient } from "../../services/external/hederaApiClient";
import { useSigner } from "../wallet/useSigner";

interface Params {
  address: string | undefined;
  token: Token | undefined;
  chain: ChainData | undefined;
}

export function useHederaTokenAssociations({ address, chain, token }: Params) {
  const publicClient = usePublicClient({
    chainId: Number(CHAIN_IDS.HEDERA),
  });

  const { evmSigner } = useSigner({ chain });
  const queryClient = useQueryClient();

  /**
   * Creates a token association transaction where the destination account authorizes to receive the given token
   */
  const associateToken = useMutation({
    mutationFn: async () => {
      try {
        if (!evmSigner) {
          throw new Error("EVM signer not found");
        }

        if (
          chain?.chainId !== CHAIN_IDS.HEDERA ||
          token?.chainId !== CHAIN_IDS.HEDERA
        ) {
          throw new Error("Chain and token to associate must be on Hedera");
        }

        const tokenAddress = parseEvmAddress(token.address);
        if (!tokenAddress) {
          throw new Error("Invalid token address");
        }

        const userAddress = parseEvmAddress(address);
        if (!userAddress) {
          throw new Error("Invalid user address");
        }

        const encodedData = encodeFunctionData({
          abi: hrc20,
          functionName: "associate",
          args: [],
        });

        const txRes = await evmSigner.sendTransaction({
          data: encodedData,
          to: tokenAddress,
          chainId: chain.chainId,
        });

        const receipt = await txRes.wait();

        if (receipt?.status !== 1) {
          throw new Error(`Transaction failed with status: ${receipt?.status}`);
        }

        return true;
      } catch (error) {
        console.error("Error associating Hedera token:", error);
        return false;
      }
    },
    async onSuccess() {
      queryClient.refetchQueries({
        queryKey: getPrefixKey(QueryKeys.IsHederaTokenAssociated),
      });
    },
  });

  /**
   * Checks if the destination account has associated the given token.
   *
   * Hedera requires accounts to associate a token before being able to receive it.
   *
   * Accounts which have max. associations set to -1 can receive any token without previous association
   */
  const isTokenAssociated = useQuery<boolean>({
    queryKey: keys().isHederaTokenAssociated(
      address,
      token?.chainId,
      token?.type,
      token?.address
    ),
    queryFn: async () => {
      if (token?.chainId !== CHAIN_IDS.HEDERA) {
        return true;
      }

      // The native HBAR token doesn't need an association
      if (token.address.toLowerCase() === nativeEvmTokenAddress.toLowerCase()) {
        return true;
      }

      if (!chain || !address || !publicClient) {
        throw new Error("Missing required parameters");
      }

      // TODO: update types
      const apiUrl = (
        chain as ChainData & {
          chainConfig?: { hedera?: { mirrorNodeUrl?: string } };
        }
      )?.chainConfig?.hedera?.mirrorNodeUrl;

      if (!apiUrl) {
        throw new Error("Missing Hedera mirror node URL in chain config");
      }

      const hederaApiClient = new HederaApiClient(apiUrl);

      return hederaApiClient.isTokenAssociated({
        address,
        token,
      });
    },
    enabled: !!address && !!publicClient && token?.chainId === CHAIN_IDS.HEDERA,
  });

  return {
    isTokenAssociated,
    associateToken,
  };
}
